/** Un élément de la barre : un numéro, ou une ellipse qui résume un trou. */
export type ElementPagination = number | 'ellipse';

/**
 * Fenêtre de pages autour de la page courante.
 *
 * Afficher TOUS les numéros devient un ruban dès quelques dizaines de pages
 * (12 boutons pour 24 lignes au départ). Règle retenue : première et dernière
 * toujours visibles, la page courante entourée d'une page de chaque côté, et une
 * ellipse là où des pages sont sautées.
 */
export function fenetrePages(pageCourante: number, total: number): ElementPagination[] {
  if (total <= 0) return [];
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

  const page = Math.min(Math.max(pageCourante, 1), total);
  const elements: ElementPagination[] = [1];

  if (page > 3) elements.push('ellipse');

  const debut = Math.max(2, page - 1);
  const fin = Math.min(total - 1, page + 1);
  for (let numero = debut; numero <= fin; numero += 1) elements.push(numero);

  if (page < total - 2) elements.push('ellipse');
  elements.push(total);

  return elements;
}

interface Props {
  readonly page: number;
  readonly pages: number;
  readonly onChange: (page: number) => void;
}

export function Pagination({ page, pages, onChange }: Props) {
  if (pages <= 1) return null;

  const elements = fenetrePages(page, pages);
  const bouton =
    'px-2 py-1 text-xs border border-slate-200 rounded disabled:text-slate-400 disabled:cursor-not-allowed disabled:hover:bg-transparent';

  return (
    <nav aria-label="Pagination des interventions" className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className={`${bouton} text-slate-600 hover:bg-slate-50`}
      >
        Précédent
      </button>

      {elements.map((element, index) =>
        element === 'ellipse' ? (
          <span
            key={`ellipse-${index}`}
            aria-hidden="true"
            className="px-2 py-1 text-xs text-slate-400"
          >
            …
          </span>
        ) : (
          <button
            key={element}
            type="button"
            onClick={() => onChange(element)}
            aria-current={element === page ? 'page' : undefined}
            aria-label={`Page ${element}`}
            className={
              element === page
                ? `${bouton} bg-red-50 text-red-700 font-medium`
                : `${bouton} text-slate-600 hover:bg-slate-50`
            }
          >
            {element}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= pages}
        className={`${bouton} text-slate-600 hover:bg-slate-50`}
      >
        Suivant
      </button>
    </nav>
  );
}
